// Client-side password policy — mirrors the Supabase Auth dashboard rules.
// Server-side enforcement (min length, character classes, leaked-password
// protection via HaveIBeenPwned) is configured in the Supabase Dashboard;
// this provides instant, friendly feedback before the request is sent.
export const PASSWORD_MIN_LENGTH = 10;

export const PASSWORD_RULES = [
  { key: "length", label: `At least ${PASSWORD_MIN_LENGTH} characters`, test: (p) => p.length >= PASSWORD_MIN_LENGTH },
  { key: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "number", label: "One number", test: (p) => /[0-9]/.test(p) },
];

export function checkPassword(password = "") {
  const checks = PASSWORD_RULES.map((r) => ({ key: r.key, label: r.label, met: r.test(password) }));
  return { valid: checks.every((c) => c.met), checks };
}

// Map a Supabase Auth error into a clear, specific message. Handles the
// server-side weak_password code plus leaked-password (HIBP) rejections.
// A simple 0–4 strength score for the meter (length + variety heuristics).
export function scorePassword(password = "") {
  if (!password) return { level: 0, label: "", pct: 0 };
  let pts = 0;
  if (password.length >= PASSWORD_MIN_LENGTH) pts += 1;
  if (password.length >= 14) pts += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) pts += 1;
  if (/[0-9]/.test(password)) pts += 1;
  if (/[^A-Za-z0-9]/.test(password)) pts += 1; // symbol bonus
  const { valid } = checkPassword(password);
  let level = Math.min(4, pts); // 1..4
  if (!valid) level = Math.min(level, 2); // never show "Strong" if a rule is unmet
  const meta = [
    { label: "", color: "#D9D3C7" },
    { label: "Weak", color: "#D9534F" },
    { label: "Fair", color: "#C98A3C" },
    { label: "Good", color: "#7A9B76" },
    { label: "Strong", color: "#25A1B0" },
  ][level];
  return { level, label: meta.label, color: meta.color, pct: (level / 4) * 100 };
}

export function interpretPasswordError(err) {
  const code = (err?.code || err?.name || "").toLowerCase();
  const msg = `${err?.message || ""} ${err?.hint || ""}`.toLowerCase();
  const isWeak = code.includes("weak_password") || code.includes("weakpassword") || msg.includes("password");
  if (isWeak) {
    if (msg.includes("pwned") || msg.includes("leak") || msg.includes("compromis") || msg.includes("breach")) {
      return "This password has appeared in a known data breach and can't be used. Please choose a different, unique password.";
    }
    if (msg.includes("length") || msg.includes("short") || msg.includes("least") || msg.includes("characters")) {
      return `Password is too short — use at least ${PASSWORD_MIN_LENGTH} characters.`;
    }
    if (msg.includes("lower") || msg.includes("upper") || msg.includes("digit") || msg.includes("number") || msg.includes("letter") || msg.includes("symbol")) {
      return "Password must include a lowercase letter, an uppercase letter, and a number.";
    }
    return `Password doesn't meet the requirements: at least ${PASSWORD_MIN_LENGTH} characters with a lowercase letter, an uppercase letter, and a number.`;
  }
  return err?.message || "Something went wrong. Please try again.";
}
